import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useEffect, useMemo, useState} from 'react';
import {hideLoader, showLoader} from '../../store/common/slice';
import {StyledCommentsModalContent} from './comments.styled';
import {Modal, Row, Col} from 'react-bootstrap';
import CommentsList from './CommentsList';
import AddComment from './AddComment';
import {StyledModal} from '../../common/shared/modal.styled';
import {selectCustomStyles} from '../../common/shared/select.styled';
import Select from 'react-select';
import {fetchBuildingData} from '../../store/buildings/actions';
import {GAEvent} from '../../common/google_analytics/google_analytics';
import { constants } from '../../common/constants/constants';
import {useAccessToken} from '../../common/hooks/useAccessToken';
import ContentLoader from '../../common/simple/ContentLoader';

export default function CommentsModal({buildingId, show, onModalHide, apartmentNum, prefetchComments = true, showApartmentSelector = true}) {
    if (!show) return '';
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [managerComments, setManagerComments] = useState([]);
    const [selectedApartment, setSelectedApartment] = useState({});
    const [apartmentOptions, setApartmentOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const client = useAccessToken();

    useEffect(async () => {
        if (client != null && !currentBuildingData.id || currentBuildingData.id !== buildingId) {
            await dispatch(fetchBuildingData({buildingID: buildingId}))
        }
    }, [buildingId, client]);

    useMemo(() => {
        const apts = currentBuildingData.apartments ?? [];
        const requiredApt = apts.find(apt => apt.apartmentNumber === apartmentNum) ?? apts[0] ?? {};
        setSelectedApartment(requiredApt);
    }, [currentBuildingData, apartmentNum]);

    useMemo(() => {
        setApartmentOptions(currentBuildingData.apartments || [])
    }, [currentBuildingData]);

    const fetchComments = async (selectedApartment) => {
        if (selectedApartment?.apartmentNumber) {
            setIsLoading(true);
            await loadComments(selectedApartment.apartmentNumber);
            setIsLoading(false);
        }
    }

    useEffect(async () => {
        if (!client) return;

        if (prefetchComments) {
            await fetchComments(selectedApartment);
        }
    }, [selectedApartment, client]);

    useEffect(() => {
        (async function () {
            if (!prefetchComments && show) {
                await fetchComments(selectedApartment);
            }
        })();
    }, [selectedApartment, show]);

    const loadComments = async (apartmentNumber) => {
        const response = await client.managers.buildings.getComments(buildingId, apartmentNumber) ?? { comments: [] };
        // Left all comments(including from bllink user) by now for testing
        setManagerComments(response.comments || []);
    }

    const createComment = async text => {
        dispatch(showLoader());

        const response = await client.put(`/managers/comments`, {
            entityType: 'apartment',
            entityId: selectedApartment.value,
            text: text
        });

        if (response.id) GAEvent(constants.GoogleAnalytics.Events.comments, 'commentAddedEvent', text, 'commentAddedSuccess');

        await loadComments(selectedApartment.apartmentNumber);
        dispatch(hideLoader());
    }

    const commentsTitle = useMemo(() => {
            const options = {apartmentNumber: !currentBuildingData?.id && apartmentNum ? apartmentNum : ''};
            return t('comments.history', options)
        }, [currentBuildingData, apartmentNum])

    const apartmentSelector = useMemo(() => {
        return showApartmentSelector ? <Row className="mb-3">
            <Col sm={4}>
                <div className="form-group">
                    <label className="label" htmlFor="apartment">{t('comments.apartments')} </label>
                    <Select
                        styles={selectCustomStyles}
                        options={apartmentOptions}
                        onChange={evt => setSelectedApartment(evt)}
                        value={selectedApartment}
                        name="apartment"
                    />
                </div>
            </Col>
        </Row> : null
    }, [showApartmentSelector, apartmentOptions, selectedApartment])

    const getModalContent = () => {
        return (
            <>
                {apartmentSelector}
                <div className='comments'>
                    <h5 className="comments-title">{commentsTitle}</h5>
                    <CommentsList
                        commentsList={managerComments}
                    />
                    <AddComment
                        className="add-comment"
                        withBorder={managerComments.length > 0}
                        onEnter={createComment}
                    />
                </div>
            </>
        )
    }

    return (
        <StyledModal
            dialogClassName={'comments-modal'}
            show={show}
            onHide={onModalHide}
            centered
        >
            <span
                className={'modal-close'}
                onClick={onModalHide}
            >
                &#215;
            </span>
            <Modal.Body>
                <h3>{t('comments.comments')}</h3>
                <StyledCommentsModalContent>
                    {isLoading ? <ContentLoader width="100%" height={200} /> : getModalContent()}
                </StyledCommentsModalContent>
            </Modal.Body>
        </StyledModal>
    )
}
